import { isHlsSupported } from '@lib/hls/support';
import { getThumbnailFromVideo } from '@lib/image/fromVideo';
import {
  acquireFrameCapture,
  listCapturedFrames,
  peekVideoFrame,
} from '@lib/media/hlsFrameCapture';
import { formatDurationToHHMMSS } from '@lib/utils/units';
import {
  THUMBNAIL_UNSUPPORTED_MESSAGE,
  useMomentThumbnailStore,
} from '@stores/MomentThumbnail/MomentThumbnailStore';
import { trackingStore } from '@stores/Tracking';
import { videoPlayerStore, VideoPlayerStore } from '@stores/VideoPlayer';
import classNames from 'classnames';
import { observer } from 'mobx-react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Grid } from 'react-window';
import { debounce } from 'throttle-debounce';
import { VideoPlayerRef } from '../VideoPlayer/VideoPlayer';
import {
  CommentMarkers,
  type TimelineCommentMark,
} from './components/CommentMarkers/CommentMarkers';
import { MomentHead } from './components/MomentHead/MomentHead';
import { TimelineZoomLevel } from './components/TimelineZoomLevel/TimelineZoomLevel';
import { TimestampNeedle } from './components/TimestampNeedle/TimestampNeedle';
import { ThumbnailQueue } from './thumbnailQueue';
import { pixelAtTime, timeAtColumn } from './timelineGeometry';
import css from './VideoTimeline.module.scss';

/**
 * Width of the frame borrowed from the player as a stand-in. It is only ever shown
 * through `blur(8px)` in a 140×90 box, so anything larger is bytes for nothing.
 */
const POSTER_MAX_WIDTH = 160;

/** Filmstrip frames are ~140px wide on screen; 280 covers hidpi. */
const FRAME_CAPTURE_OPTIONS = {
  quality: 0.4,
  maxWidth: 280,
  // Frame spacing gets as tight as ~0.017s at maximum zoom on short media, so
  // the capture layer's default 0.1s bucketing would merge neighbouring frames
  // and the strip would visibly repeat itself.
  timestampQuantum: 0.01,
} as const;

type VirtualizedVideoTimelineProps = {
  duration: number;
  videoPlayerRef: React.RefObject<VideoPlayerRef | null>;
  store?: VideoPlayerStore;
  mediaId: string;
  libraryId: string;
  compactZoom?: boolean;
  zoomLevel?: number;
  onZoomLevelChange?: (level: number) => void;
  /** Comment anchors to draw along the strip, in absolute media seconds. */
  comments?: TimelineCommentMark[];
  selectedCommentId?: string | null;
  onSelectComment?: (commentId: string) => void;
};

// Separate component for FrameThumbnail that can use hooks
const FrameThumbnailComponent = ({
  frameIndex,
  timestamp,
  cacheKey,
  thumbnail,
  placeholder,
  queue,
}: {
  frameIndex: number;
  timestamp: number;
  cacheKey: string;
  thumbnail: string | null;
  /**
   * Blurred stand-in while this frame loads — the nearest already-captured frame
   * if there is one, otherwise the player's own decoded frame. Already resolved by
   * the parent; this component doesn't care which it got.
   */
  placeholder: string | null;
  queue: ThumbnailQueue | null;
}) => {
  const abortControllerRef = useRef<AbortController | null>(null);

  // Ask for this frame while the cell is on screen. The queue decides when to
  // actually produce it — it holds requests while the video plays or a trim
  // handle is being dragged, so this effect must not gate on playback itself:
  // it wouldn't re-run when playback stopped, and the request would be lost.
  useEffect(() => {
    if (!thumbnail && queue) {
      abortControllerRef.current = queue.addRequest(frameIndex, timestamp, cacheKey);
    }

    // Cleanup on unmount
    return () => {
      if (abortControllerRef.current && queue) {
        queue.removeRequest(cacheKey);
        abortControllerRef.current = null;
      }
    };
  }, [frameIndex, timestamp, cacheKey, thumbnail, queue]);

  return (
    <div className={css.frame}>
      {thumbnail ? (
        <img src={thumbnail} alt={`Frame at ${timestamp.toFixed(2)}s`} />
      ) : placeholder ? (
        <div className={css.blurredPlaceholder}>
          <img
            src={placeholder}
            alt={`Blurred placeholder for frame at ${timestamp.toFixed(2)}s`}
            className={css.blurredImage}
          />
          <div className={css.pulseOverlay} />
        </div>
      ) : (
        <div className={css.loadingFrame} />
      )}
    </div>
  );
};

export const VirtualizedVideoTimeline = observer(function VirtualizedVideoTimeline({
  duration,
  videoPlayerRef,
  store = videoPlayerStore,
  mediaId,
  libraryId,
  compactZoom,
  zoomLevel: externalZoomLevel,
  onZoomLevelChange,
  comments,
  selectedCommentId = null,
  onSelectComment,
}: VirtualizedVideoTimelineProps) {
  // All hooks must run unconditionally (Rules of Hooks). Early return after hooks.
  const hlsSupported = isHlsSupported();
  const thumbnailStore = useMomentThumbnailStore();

  //keeping track of things
  const [containerWidth, setContainerWidth] = useState(800);
  const [internalZoomLevel, setInternalZoomLevel] = useState(externalZoomLevel ?? 100);
  const zoomLevel = externalZoomLevel ?? internalZoomLevel;
  const onZoomLevelChangeRef = useRef(onZoomLevelChange);
  onZoomLevelChangeRef.current = onZoomLevelChange;
  const setZoomLevel = useCallback((level: number) => {
    setInternalZoomLevel(level);
    onZoomLevelChangeRef.current?.(level);
  }, []);
  const [scrollLeft, setScrollLeft] = useState(0);
  const [targetCenterTime, setTargetCenterTime] = useState<number | null>(null);

  const hasAutoZoomedRef = useRef(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const gridRef = useRef<any>(null);
  const frameWidth = 140;

  // Zoom state tracking
  const [isZooming, setIsZooming] = useState(false);

  // Frames are not kept here. The capture module owns them, holds them as Blobs
  // behind object URLs, and revokes each one as it evicts — so a second copy of
  // those URLs in React state would eventually point at revoked Blobs. Instead
  // this counter just says "something new arrived, render again", and every cell
  // reads through to the module. Reading is also what marks a frame as recently
  // used, which is what keeps on-screen frames from being the ones evicted.
  const [framesReady, setFramesReady] = useState(0);

  const handleThumbnailGenerated = useCallback(() => {
    setFramesReady((n) => n + 1);
  }, []);

  // Generation should stop while the user is watching or dragging a trim
  // handle — those are the moments the decoder and network are busiest. Note
  // dragging does not pause playback (the scrub deliberately resumes it), so
  // both flags have to be checked; neither implies the other.
  //
  // The gate only ever defers *speculative* work: it waits until roughly one
  // screenful of frames exists before it will hold anything back. The user
  // always gets a usable strip for what they are looking at; it is the frames
  // revealed by later zooming and scrolling that yield to playback.
  //
  // That is also the safety property. Both flags live on a `VideoPlayerStore`
  // that is sometimes the app-wide singleton, and a flag describes the last
  // thing that happened, not the state of the world. One left stuck true used to
  // mean a blank filmstrip for the rest of the session with no way to recover;
  // now the worst case is a first screenful and no prefetch.
  const visibleFrameCount = Math.max(1, Math.ceil(containerWidth / frameWidth));
  const isBusy = store.isPlaying || store.isDragging;

  // Also hold everything back until the layout stops being a placeholder.
  //
  // The first render runs at 100% zoom against an assumed 800px width, so its
  // cells span the *whole* media — frames tens or hundreds of seconds apart, each
  // needing its own fragment, which is the most expensive kind of capture there
  // is. Auto-zoom then throws every one of them away. Worse, the first of them to
  // reach the capture layer is what the hidden player gets positioned at, so the
  // player is built to serve requests that no longer exist and the first real
  // frame pays for a long seek to somewhere else entirely.
  const [layoutSettled, setLayoutSettled] = useState(false);
  useEffect(() => {
    // Unconditional, and a macrotask so it lands after the auto-zoom and
    // container-measurement effects have re-rendered. Never gate this on
    // auto-zoom actually happening — a strip that waits for something optional
    // is a strip that can wait forever.
    const id = setTimeout(() => setLayoutSettled(true), 0);
    return () => clearTimeout(id);
  }, []);

  const shouldPauseThumbnails = !layoutSettled || (isBusy && framesReady >= visibleFrameCount);
  const shouldPauseThumbnailsRef = useRef(shouldPauseThumbnails);
  shouldPauseThumbnailsRef.current = shouldPauseThumbnails;

  // One queue per media. Thumbnails need MSE, so there's nothing to build
  // without it. The capture lease is taken here and released in the cleanup, so
  // the hidden player is freed when the last surface using it goes away.
  const [thumbnailQueue, setThumbnailQueue] = useState<ThumbnailQueue | null>(null);
  useEffect(() => {
    if (!hlsSupported) {
      setThumbnailQueue(null);
      return;
    }
    const releaseCapture = acquireFrameCapture();
    const queue = new ThumbnailQueue(
      (timestamp, signal) =>
        thumbnailStore.getThumbnail(libraryId, mediaId, timestamp, FRAME_CAPTURE_OPTIONS, signal),
      handleThumbnailGenerated,
      { paused: shouldPauseThumbnailsRef.current },
    );
    setThumbnailQueue(queue);
    return () => {
      queue.dispose();
      setThumbnailQueue(null);
      releaseCapture();
    };
  }, [hlsSupported, thumbnailStore, libraryId, mediaId, handleThumbnailGenerated]);

  useEffect(() => {
    thumbnailQueue?.setPaused(shouldPauseThumbnails);
  }, [thumbnailQueue, shouldPauseThumbnails]);

  // Calculate total timeline width and number of frames needed for full timeline
  const timelineWidth = useMemo(() => {
    return (zoomLevel / 100) * containerWidth;
  }, [zoomLevel, containerWidth]);

  const totalFrames = useMemo(() => {
    return Math.ceil(timelineWidth / frameWidth);
  }, [timelineWidth, frameWidth]);

  // Every column→time conversion goes through this, so cells, labels, the needle and
  // the moment head all share one mapping. Deriving a column's time from its index
  // over `totalFrames` instead looks equivalent but is not: `totalFrames` is a
  // `ceil`, so it drifts against the pixel mapping the needle uses, by up to a whole
  // column at the right-hand end. See `timelineGeometry.ts`.
  const geometry = useMemo(
    () => ({ frameWidth, timelineWidth, duration }),
    [frameWidth, timelineWidth, duration],
  );

  // Cache key for a frame. Just the timestamp: the cache is emptied whenever the
  // media changes, so library and media id would only be constant padding — and
  // they're UUIDs, which made the old key un-parseable without splitting on the
  // same '-' the UUIDs contain.
  const getThumbnailCacheKey = useCallback(
    (frameIndex: number) => timeAtColumn(frameIndex, geometry).toFixed(2),
    [geometry],
  );

  // Frames captured so far, in timestamp order, so the nearest-neighbour lookup
  // below can binary search instead of walking every frame once per visible cell
  // per render. Recomputed only when a new frame lands or the media changes.
  const capturedFrames = useMemo(
    // eslint-disable-next-line react-hooks/exhaustive-deps -- framesReady is the invalidation signal
    () => listCapturedFrames(libraryId, mediaId, FRAME_CAPTURE_OPTIONS),
    [libraryId, mediaId, framesReady],
  );

  /** Closest already-captured frame, used as a blurred stand-in while one loads. */
  const findNearestThumbnail = useCallback(
    (timestamp: number) => {
      if (capturedFrames.length === 0) return null;

      let low = 0;
      let high = capturedFrames.length - 1;
      while (low < high) {
        const mid = (low + high) >> 1;
        if (capturedFrames[mid].timestamp < timestamp) {
          low = mid + 1;
        } else {
          high = mid;
        }
      }

      // `low` is the first entry at or after the target; its predecessor may
      // still be closer.
      let nearest = capturedFrames[low];
      if (low > 0) {
        const before = capturedFrames[low - 1];
        if (Math.abs(before.timestamp - timestamp) < Math.abs(nearest.timestamp - timestamp)) {
          nearest = before;
        }
      }

      return nearest.url;
    },
    [capturedFrames],
  );

  // A single frame borrowed from the player next door, used as the blurred stand-in
  // until real frames start landing.
  //
  // The strip's cold start is a serial chain — token, manifest, init segment,
  // fragment, decode — during which `findNearestThumbnail` has nothing to offer and
  // every cell falls through to a bare shimmer bar. Meanwhile the modal's video has
  // already decoded and painted a frame. Borrowing it costs one small canvas draw
  // and fills exactly that window.
  //
  // Deliberately not stored in the frame cache and not counted as a captured frame:
  // it is one image standing in for the whole strip, so letting it into
  // `capturedFrames` would poison both the nearest-neighbour search and the byte
  // budget.
  const [posterFrame, setPosterFrame] = useState<string | null>(null);
  useEffect(() => {
    setPosterFrame(null);

    const video = videoPlayerRef.current?.video;
    if (!video) return;

    const grab = () => {
      // Must fail to *nothing* rather than to a black rectangle: drawing an
      // undecoded element yields a blank or 0×0 canvas.
      if (video.readyState < 2 || !video.videoWidth) return;
      try {
        const frame = getThumbnailFromVideo(video, 0.5, POSTER_MAX_WIDTH);
        if (frame) setPosterFrame(frame);
      } catch {
        // `toDataURL` throws SecurityError on a tainted canvas, which is reachable
        // wherever the player falls back to a cross-origin `video.src` instead of
        // MSE. Uncaught it would propagate out of this effect and take the tree
        // down over a decoration — the placeholder is the only thing worth losing.
      }
    };

    grab();
    // The `isLoading` gate upstream means the player has already reported ready by
    // the time this component mounts, so the immediate grab is the expected path and
    // this listener is only a backstop.
    video.addEventListener('loadeddata', grab, { once: true });
    return () => video.removeEventListener('loadeddata', grab);
  }, [libraryId, mediaId, videoPlayerRef]);

  // Calculate appropriate zoom level for the moment
  const calculateAutoZoomLevel = useCallback(() => {
    if (!store.moment || !containerWidth || !duration) return 100;

    const moment = store.moment;
    const momentDuration = moment.end - moment.start;

    // Target the moment to take up about 60% of the visible timeline width
    const targetMomentWidthPercent = 0.6;
    const momentPercent = momentDuration / duration;

    // Calculate zoom level needed to make the moment take up the target width
    const requiredZoom = (targetMomentWidthPercent / momentPercent) * 100;

    // Clamp between min and max zoom levels
    return Math.max(100, Math.min(10000, Math.round(requiredZoom)));
  }, [duration, containerWidth, store.moment]);

  const centerTimelineOnMoment = useCallback(() => {
    if (isZooming) {
      return;
    }
    if (!gridRef.current?.element || !store.moment) {
      return;
    }

    const moment = store.moment;

    // Calculate moment width in pixels
    const momentDuration = moment.end - moment.start;
    const momentWidthInPixels = (momentDuration / duration) * timelineWidth;

    // Don't center if moment width is greater than container width
    if (momentWidthInPixels > containerWidth) {
      return;
    }

    const momentCenter = (moment.start + moment.end) / 2;

    // Position where the moment center should be (center of visible area)
    const targetScrollPosition = pixelAtTime(momentCenter, geometry) - containerWidth / 2;

    // Ensure we don't scroll beyond the boundaries
    const maxScroll = timelineWidth - containerWidth;
    const finalScrollPosition = Math.max(0, Math.min(targetScrollPosition, maxScroll));

    // Calculate the scroll position to center the moment
    const scrollableContainer = gridRef.current.element;

    // Try both methods to ensure scrolling works
    scrollableContainer.scrollLeft = finalScrollPosition;
    scrollableContainer.scrollTo({
      left: finalScrollPosition,
    });
  }, [timelineWidth, containerWidth, duration, store.moment]);

  // Declarative centering function for specific time
  const centerTimelineOnTime = useCallback(
    (time: number) => {
      if (isZooming || !gridRef.current?.element) {
        return;
      }

      // Calculate timestamp position in pixels
      const timestampPosition = pixelAtTime(time, geometry);

      // Position where the timestamp should be (center of visible area)
      const targetScrollPosition = timestampPosition - containerWidth / 2;

      // Ensure we don't scroll beyond the boundaries
      const maxScroll = timelineWidth - containerWidth;
      const finalScrollPosition = Math.max(0, Math.min(targetScrollPosition, maxScroll));

      // Calculate the scroll position to center the timestamp
      const scrollableContainer = gridRef.current.element;

      // Try both methods to ensure scrolling works
      scrollableContainer.scrollLeft = finalScrollPosition;
      scrollableContainer.scrollTo({
        left: finalScrollPosition,
      });
    },
    [timelineWidth, containerWidth, duration, isZooming],
  );

  // Handle declarative centering requests
  const handleRequestCenterOnTime = useCallback((time: number) => {
    setTargetCenterTime(time);
  }, []);

  const centerTimelineOnCurrentTimestamp = useCallback(() => {
    if (!gridRef.current?.element || !videoPlayerRef.current) {
      return;
    }

    const currentTime = store.currentTime;

    // Calculate timestamp position in pixels
    const timestampPosition = pixelAtTime(currentTime, geometry);

    // Position where the timestamp should be (center of visible area)
    const targetScrollPosition = timestampPosition - containerWidth / 2;

    // Ensure we don't scroll beyond the boundaries
    const maxScroll = timelineWidth - containerWidth;
    const finalScrollPosition = Math.max(0, Math.min(targetScrollPosition, maxScroll));

    // Calculate the scroll position to center the timestamp
    const scrollableContainer = gridRef.current.element;

    // Try both methods to ensure scrolling works
    scrollableContainer.scrollLeft = finalScrollPosition;
    scrollableContainer.scrollTo({
      left: finalScrollPosition,
    });
  }, [timelineWidth, containerWidth, duration, store.currentTime, videoPlayerRef]);

  const handleZoomLevelChange = useCallback(
    (value: number) => {
      setZoomLevel(value);
    },
    [setZoomLevel],
  );

  const handleZoomLevelCommitted = useCallback(
    (value: number) => {
      setZoomLevel(value);
      trackingStore.timelineZoomChanged({ zoomLevel: value });
    },
    [setZoomLevel],
  );

  const handleZoomDragStart = useCallback(() => {
    setIsZooming(true);
  }, []);

  const handleZoomDragEnd = useCallback(() => {
    setIsZooming(false);
  }, []);

  // Declarative centering effect - triggers when timeline width changes and we have a target time
  useEffect(() => {
    if (targetCenterTime !== null && !isZooming && !store.isDragging) {
      centerTimelineOnTime(targetCenterTime);
      // Reset the target time so this doesn't run again
      setTargetCenterTime(null);
    }
  }, [timelineWidth, targetCenterTime, centerTimelineOnTime, isZooming, store.isDragging]);

  // Handle resize to update container width
  const handleResize = useCallback(
    debounce(100, () => {
      if (containerRef.current) {
        setContainerWidth(containerRef.current.clientWidth);
      }
    }),
    [],
  );

  useEffect(() => {
    // Initial container width - use the thumbnail container which is the visible area
    if (containerRef.current) {
      setContainerWidth(containerRef.current.clientWidth);
    }

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [handleResize]);

  // Auto-zoom effect - runs once when component mounts and moment is available
  // Skip if an external zoom level is provided (e.g. restored from parent state)
  useEffect(() => {
    if (!hasAutoZoomedRef.current && store.moment && containerWidth && duration) {
      // Only auto-zoom if parent hasn't provided a saved zoom level
      if (externalZoomLevel === undefined) {
        const autoZoomLevel = calculateAutoZoomLevel();
        handleZoomLevelChange(autoZoomLevel);
      }
      hasAutoZoomedRef.current = true;

      // Always center on the moment's center time
      const momentCenterTime = (store.moment.start + store.moment.end) / 2;
      setTargetCenterTime(momentCenterTime);
    }
  }, [
    store.moment,
    containerWidth,
    duration,
    calculateAutoZoomLevel,
    handleZoomLevelChange,
    externalZoomLevel,
  ]);

  // Handle scroll events to update visible area with requestAnimationFrame for smooth updates
  const scrollAnimationRef = useRef<number | null>(null);
  const pendingScrollUpdate = useRef<number | null>(null);

  const handleScroll = useCallback(
    (e: Event) => {
      const target = e.target as HTMLDivElement;
      const newScrollLeft = target.scrollLeft;

      // Cancel any pending animation frame
      if (scrollAnimationRef.current) {
        cancelAnimationFrame(scrollAnimationRef.current);
      }

      // Store the latest scroll position
      pendingScrollUpdate.current = newScrollLeft;

      // Schedule update for next frame
      scrollAnimationRef.current = requestAnimationFrame(() => {
        if (pendingScrollUpdate.current !== null) {
          setScrollLeft(pendingScrollUpdate.current);
          pendingScrollUpdate.current = null;
        }
        scrollAnimationRef.current = null;
      });
    },
    // Nothing from the render scope is read any more — it only records scrollLeft.
    [],
  );

  useEffect(() => {
    // Listen to Grid's scroll container instead of outer container
    const setupScrollListener = () => {
      const scrollContainer = gridRef.current?.element;
      if (scrollContainer) {
        scrollContainer.addEventListener('scroll', handleScroll);
        return () => {
          scrollContainer.removeEventListener('scroll', handleScroll);
          // Cleanup animation frame on unmount
          if (scrollAnimationRef.current) {
            cancelAnimationFrame(scrollAnimationRef.current);
          }
        };
      } else {
        // Retry after a short delay if Grid ref isn't ready yet
        setTimeout(setupScrollListener, 100);
      }
    };

    const cleanup = setupScrollListener();
    return cleanup;
  }, [handleScroll]);

  // Add this handler to allow horizontal scrolling with mouse wheel, touchpad, and Shift+scroll
  const handleWheel = useCallback((e: WheelEvent) => {
    const container = gridRef.current?.element;
    if (!container) return;

    // Handle horizontal scrolling (touchpad horizontal scroll or Shift+scroll)
    if (e.deltaX !== 0) {
      e.preventDefault();
      container.scrollLeft += e.deltaX;
    }
    // Handle vertical scrolling as horizontal (original behavior)
    else if (e.deltaY !== 0) {
      e.preventDefault();
      container.scrollLeft += e.deltaY;
    }
  }, []);

  // Add wheel event listener with passive: false to allow preventDefault
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    container.addEventListener('wheel', handleWheel, { passive: false });

    return () => {
      container.removeEventListener('wheel', handleWheel);
    };
  }, [handleWheel]);

  // Component for individual frame thumbnails with virtualized rendering
  const FrameThumbnail = useCallback(
    ({ frameIndex }: { frameIndex: number }) => {
      const timestamp = timeAtColumn(frameIndex, geometry);
      const cacheKey = getThumbnailCacheKey(frameIndex);

      // Read through to the capture module rather than a local copy — this both
      // avoids holding object URLs we don't own and marks the frame as recently
      // used, so what's on screen is never what gets evicted.
      const thumbnail = peekVideoFrame(libraryId, mediaId, timestamp, FRAME_CAPTURE_OPTIONS);

      // A real captured frame is a better stand-in than the borrowed one, so the
      // poster only fills the window before anything has been captured.
      const placeholder = thumbnail ? null : (findNearestThumbnail(timestamp) ?? posterFrame);

      return (
        <FrameThumbnailComponent
          frameIndex={frameIndex}
          timestamp={timestamp}
          cacheKey={cacheKey}
          thumbnail={thumbnail}
          placeholder={placeholder}
          queue={thumbnailQueue}
        />
      );
    },
    [
      geometry,
      libraryId,
      mediaId,
      posterFrame,
      getThumbnailCacheKey,
      findNearestThumbnail,
      thumbnailQueue,
    ],
  );

  // Generate timestamp data for virtualized rendering
  const timestampData = useMemo(() => {
    const timestampInterval = Math.max(1, Math.floor((duration * 1.25) / totalFrames));
    const timestamps = [];
    const dots = [];

    for (let time = 0; time <= duration; time += timestampInterval) {
      timestamps.push({
        time,
        position: (time / duration) * 100,
        isFirst: time === 0,
      });

      // Add dots between timestamps
      for (let i = 1; i <= 4; i++) {
        const dotTime = time + (timestampInterval * i) / 5;
        if (dotTime < duration) {
          dots.push({
            time: dotTime,
            position: (dotTime / duration) * 100,
          });
        }
      }
    }

    return { timestamps, dots };
  }, [duration, totalFrames]);

  // Calculate column width ensuring the sum of all columns equals timelineWidth
  const getColumnWidth = useCallback(
    (columnIndex: number) => {
      if (columnIndex === totalFrames - 1) {
        // For the last column, calculate remaining width
        const totalPrevWidth = (totalFrames - 1) * frameWidth;
        const remainingWidth = timelineWidth - totalPrevWidth;
        return Math.max(1, remainingWidth);
      }
      return frameWidth;
    },
    [totalFrames, frameWidth, timelineWidth],
  );

  // React-window cell renderer for horizontal grid
  const renderCell = useCallback(
    ({
      columnIndex,
      rowIndex,
      style,
    }: {
      columnIndex: number;
      rowIndex: number;
      style: React.CSSProperties;
    }) => {
      if (rowIndex === 0) {
        // Timestamp row
        const frameStartTime = timeAtColumn(columnIndex, geometry);
        const frameEndTime = timeAtColumn(columnIndex + 1, geometry);

        // Find timestamps and dots that fall within this frame
        const frameTimestamps = timestampData.timestamps.filter(
          (ts) => ts.time >= frameStartTime && ts.time < frameEndTime,
        );
        const frameDots = timestampData.dots.filter(
          (dot) => dot.time >= frameStartTime && dot.time < frameEndTime,
        );

        return (
          <div
            style={{
              ...style,
              width: getColumnWidth(columnIndex),
            }}
            className={css.timestampFrame}
          >
            {frameTimestamps.map((ts) => (
              <div
                key={ts.time}
                className={classNames(css.timestamp, { [css.first]: ts.isFirst })}
                style={{
                  left: `${((ts.time - frameStartTime) / (frameEndTime - frameStartTime)) * 100}%`,
                }}
              >
                {ts.time === 0 ? '0s' : formatDurationToHHMMSS(ts.time)}
              </div>
            ))}
            {frameDots.map((dot) => (
              <div
                key={dot.time}
                className={css.dot}
                style={{
                  left: `${((dot.time - frameStartTime) / (frameEndTime - frameStartTime)) * 100}%`,
                }}
              />
            ))}
          </div>
        );
      } else {
        // Thumbnail row
        return (
          <div
            style={{
              ...style,
              width: getColumnWidth(columnIndex),
              overflow: 'hidden',
            }}
          >
            <FrameThumbnail frameIndex={columnIndex} />
          </div>
        );
      }
    },
    [FrameThumbnail, geometry, timestampData, getColumnWidth],
  );

  if (duration <= 0.1) return null;

  // Built once and rendered by both branches below — the strip has a fallback layout for
  // devices without MSE, and a marker missing from one of them is a comment that cannot be
  // reached there at all.
  const commentMarkers =
    comments?.length && onSelectComment ? (
      <CommentMarkers
        timelineWidth={timelineWidth}
        duration={duration}
        marks={comments}
        selectedCommentId={selectedCommentId}
        onSelect={onSelectComment}
      />
    ) : null;

  const momentHeadProps = {
    timelineWidth,
    visibleContainerWidth: containerWidth,
    videoPlayerRef,
    // When HLS.js is not supported (older iOS without MSE), we don't provide a scrollableContainerRef
    // because the fallback div doesn't have a ref attached. This breaks scroll functionality for those devices.
    // NOTE: We currently don't care about older iOS devices without MSE (< iOS 17.1) since they can't generate
    // thumbnails anyway. If we ever want to support those devices with native HLS playback, then we should do something here.
    scrollableContainerRef: hlsSupported ? gridRef : undefined,
    scrollLeft,
    timelineStartTime: 0,
    timelineDuration: duration,
    store,
    onZoomChange: handleZoomLevelChange,
    onCenterOnMoment: centerTimelineOnMoment,
    onRequestCenterOnTime: handleRequestCenterOnTime,
  };

  return (
    <div>
      <div className={css.timelineContainer}>
        {compactZoom && (
          <div className={css.zoomLevelContainer}>
            <TimelineZoomLevel
              onChange={handleZoomLevelChange}
              onChangeCommitted={handleZoomLevelCommitted}
              onCenterOnTimestamp={centerTimelineOnCurrentTimestamp}
              onDragStart={handleZoomDragStart}
              onDragEnd={handleZoomDragEnd}
              value={zoomLevel}
              minZoom={100}
              maxZoom={10000}
              compact
            />
          </div>
        )}
        <div className={css.timelineScrollableContainer}>
          <div ref={containerRef} className={css.thumbnailContainer}>
            {!hlsSupported ? (
              <div
                className={css.virtualizedList}
                style={{
                  width: timelineWidth,
                  overflowX: 'auto',
                  overflowY: 'hidden',
                  minHeight: 120,
                }}
              >
                <MomentHead {...momentHeadProps} />
                <TimestampNeedle
                  timelineWidth={timelineWidth}
                  duration={duration}
                  store={store}
                  isZooming={isZooming}
                />
                {commentMarkers}
                <div className={css.thumbnailsUnsupportedMessage}>
                  {THUMBNAIL_UNSUPPORTED_MESSAGE}
                </div>
              </div>
            ) : (
              <Grid
                gridRef={gridRef}
                cellComponent={renderCell as any}
                cellProps={{}}
                columnCount={totalFrames}
                columnWidth={getColumnWidth}
                rowCount={2}
                rowHeight={(index) => (index === 0 ? 30 : 90)}
                overscanCount={2}
                className={css.virtualizedList}
                style={{
                  width: timelineWidth,
                  overflowX: 'auto',
                  overflowY: 'hidden',
                }}
              >
                <MomentHead {...momentHeadProps} />
                <TimestampNeedle
                  timelineWidth={timelineWidth}
                  duration={duration}
                  store={store}
                  isZooming={isZooming}
                />
                {commentMarkers}
              </Grid>
            )}
          </div>
        </div>
        {!compactZoom && (
          <div className={css.zoomLevelContainer}>
            <TimelineZoomLevel
              style={{ marginLeft: 'auto' }}
              onChange={handleZoomLevelChange}
              onChangeCommitted={handleZoomLevelCommitted}
              onCenterOnTimestamp={centerTimelineOnCurrentTimestamp}
              onDragStart={handleZoomDragStart}
              onDragEnd={handleZoomDragEnd}
              value={zoomLevel}
              minZoom={100}
              maxZoom={10000}
            />
          </div>
        )}
      </div>
    </div>
  );
});
